import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';

export interface Crumb {
  label: string;
  /** Omit on the last (current) crumb — it renders as plain text. */
  to?: string;
}

interface BreadcrumbsProps {
  items: Crumb[];
  /**
   * `dark` is for the navy course/bundle hero (bg-primary-900); `light` is for
   * the ordinary paper shell. This is not the staff `Breadcrumb`
   * (components/admin/Breadcrumb.tsx) — that one is scoped to staff chrome and
   * carries its own greys plus `dark:` variants. Keeping them separate is the
   * "three systems, do not merge" rule, not duplication by accident.
   */
  tone?: 'dark' | 'light';
  className?: string;
}

const TONES = {
  dark: {
    link: 'text-primary-200/75 hover:text-white',
    current: 'text-primary-100',
    sep: 'text-primary-500/70',
  },
  light: {
    link: 'text-gray-500 hover:text-gray-900',
    current: 'text-gray-700',
    sep: 'text-gray-300',
  },
} as const;

/**
 * Student-shell breadcrumb trail. Renders nothing for a single crumb — a lone
 * "you are here" with nowhere to go is chrome, not navigation, so callers can
 * hand over whatever trail they derived without guarding it themselves.
 */
export const Breadcrumbs: React.FC<BreadcrumbsProps> = ({ items, tone = 'light', className = '' }) => {
  if (items.length < 2) return null;
  const t = TONES[tone];

  return (
    <nav aria-label="Breadcrumb" className={className}>
      <ol className="flex items-center gap-1.5 text-xs font-semibold min-w-0">
        {items.map((item, i) => {
          const isLast = i === items.length - 1;
          return (
            <li key={`${item.label}-${i}`} className={`flex items-center gap-1.5 min-w-0 ${isLast ? 'truncate' : 'flex-shrink-0'}`}>
              {i > 0 && <ChevronRight className={`h-3 w-3 flex-shrink-0 ${t.sep}`} aria-hidden />}
              {item.to && !isLast ? (
                <Link to={item.to} className={`${t.link} transition-colors`}>
                  {item.label}
                </Link>
              ) : (
                <span className={`${t.current} truncate`} aria-current={isLast ? 'page' : undefined}>
                  {item.label}
                </span>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
};
